import { random } from 'lodash/fp';
import React, { useMemo } from 'react';

import {
  DeviceModelType,
  useDeviceModels,
  useFeatureFlags,
} from '@portals/api/partners';
import { isPartnerFeatureOn } from '@portals/framework';
import { ReactComponent as Activity } from '@portals/icons/linear/activity.svg';
import { ReactComponent as Box } from '@portals/icons/linear/box.svg';
import { ReactComponent as Clock2 } from '@portals/icons/linear/clock-2.svg';
import { ReactComponent as DocumentText } from '@portals/icons/linear/document-text.svg';
import { ReactComponent as DollarSquare } from '@portals/icons/linear/dollar-square.svg';
import { ReactComponent as Like1 } from '@portals/icons/linear/like-1.svg';
import { ReactComponent as Refresh } from '@portals/icons/linear/refresh.svg';
import { COMPANIES, MODELS, PRODUCT_FAMILIES, PRODUCTS } from '@portals/mocks';
import { TrendEnum, useTheme } from '@portals/ui';
import { hexToRgba } from '@portals/utils';

export const useOverviewCardsList = () => {
  const featureFlags = useFeatureFlags();

  return useMemo(
    () => [
      {
        title: 'Active Licenses',
        color: 'green',
        icon: <Activity />,
        inProgress: ['getLicense'],
        value: 10,
        deltas: [
          {
            value: '+3 in Last 30 Days',
            trend: TrendEnum.Up,
          },
        ],
      },
      {
        title: isPartnerFeatureOn(featureFlags, 'volvo_demo')
          ? 'Trucks'
          : 'Devices',
        color: 'pink',
        inProgress: ['getDevices'],
        icon: <Box />,
        value: 10,
        deltas: [
          {
            value: '+3 in Last 30 Days',
            trend: TrendEnum.Up,
          },
        ],
      },
      {
        title:
          isPartnerFeatureOn(featureFlags, 'volvo_demo') ||
          isPartnerFeatureOn(featureFlags, 'ansa')
            ? 'Daily Avg. Hours'
            : 'Devices with Licenses',
        color: 'cyan',
        inProgress: ['getDevices', 'getLicense'],
        icon:
          isPartnerFeatureOn(featureFlags, 'volvo_demo') ||
          isPartnerFeatureOn(featureFlags, 'ansa') ? (
            <Clock2 />
          ) : (
            <DocumentText />
          ),
        value:
          isPartnerFeatureOn(featureFlags, 'volvo_demo') ||
          isPartnerFeatureOn(featureFlags, 'ansa')
            ? '53 hours'
            : '56kg',
        deltas: [
          {
            value:
              isPartnerFeatureOn(featureFlags, 'volvo_demo') ||
              isPartnerFeatureOn(featureFlags, 'ansa')
                ? '+4 hours in Last 30 Days'
                : '+197 in Last 30 Days',
            trend: TrendEnum.Up,
          },
        ],
      },
      {
        title:
          isPartnerFeatureOn(featureFlags, 'volvo_demo') ||
          isPartnerFeatureOn(featureFlags, 'ansa')
            ? 'Daily Avg. km'
            : 'MRR',
        color: 'red',
        icon:
          isPartnerFeatureOn(featureFlags, 'volvo_demo') ||
          isPartnerFeatureOn(featureFlags, 'ansa') ? (
            <Like1 />
          ) : (
            <Refresh />
          ),
        value:
          isPartnerFeatureOn(featureFlags, 'volvo_demo') ||
          isPartnerFeatureOn(featureFlags, 'ansa')
            ? '35 km'
            : '$46,430',
        inProgress: ['getDevices', 'getLicense'],
        deltas: [
          {
            value:
              isPartnerFeatureOn(featureFlags, 'volvo_demo') ||
              isPartnerFeatureOn(featureFlags, 'ansa')
                ? '-1 km in Last 30 Days'
                : '+$3,201 in Last 30 Days',
            trend: TrendEnum.Up,
          },
        ],
      },
      {
        title: 'Financing',
        color: 'blue',
        icon: <DollarSquare />,
        value: '$40,000',
        deltas:
          isPartnerFeatureOn(featureFlags, 'volvo_demo') ||
          isPartnerFeatureOn(featureFlags, 'ansa')
            ? [{ value: 'Received: +$10,000' }, { value: 'Repaid: -$8,000' }]
            : [{ value: 'Received: +$65,000' }, { value: 'Repaid: -$60,000' }],
      },
    ],
    [featureFlags]
  );
};

export const useRevenueByProductData = (modelId?: string) => {
  const { color } = useTheme();

  const models = useDeviceModels();

  return useMemo(() => {
    if (modelId) {
      return PRODUCTS.map((_, index) => ({
        label: PRODUCTS[index % PRODUCTS.length],
        value: random(4000, 100000),
        fill: hexToRgba(color['primary500'], 100 - index * 10),
      }));
    }

    return (models?.data || []).map(({ model }: DeviceModelType, index) => ({
      label: MODELS[index % MODELS.length],
      value: random(4000, 100000),
      fill: hexToRgba(color['primary500'], 100 - index * 10),
    }));
  }, [color, modelId, models]);
};

export const useRevenueByProductFamily = () => {
  const { color } = useTheme();

  return useMemo(
    () =>
      PRODUCT_FAMILIES.map((family, index) => ({
        label: family,
        value: random(4000, 100000),
        fill: hexToRgba(color['secondary500'], 100 - index * 10),
      })),
    [color]
  );
};

export const useCoffeeUsage = () => {
  const { color } = useTheme();

  return useMemo(
    () =>
      COMPANIES.map((company, index) => ({
        label: company,
        value: random(10, 300),
        fill: hexToRgba(color['secondary500'], 100 - index * 10),
      })),
    [color]
  );
};
